import React, { useEffect, useId, useMemo, useState } from 'react';
import { Checkbox, FormControlLabel } from '@mui/material';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import styles from './VehicleManagementDrawer.module.css';
import { useModal } from '../../store/ModalProvider';
import type {
  DriversWithoutRestModalData,
  DriversVehiclesAvailabilityModalData,
  DriverServiceTotalsModalData,
  ActivityDeclarationsModalData,
  RestAfterWorkModalData,
  TachographServiceTypeModalData,
  ChapasPorMotoristasModalData,
  NonScheduledDriversModalData,
} from '../../types/modal';

type DriversManagementDrawerProps = {
  open: boolean;
  onClose: () => void;
};

type AccordionSectionProps = {
  id: string;
  title: string;
  isOpen: boolean;
  onToggle: () => void;
  children?: React.ReactNode;
  className?: string;
  headerContent?: React.ReactNode;
};

const AccordionSection = ({
  id,
  title,
  isOpen,
  onToggle,
  children,
  className,
  headerContent,
}: AccordionSectionProps) => (
  <div className={`${styles.section} ${className ?? ''}`.trim()}>
    <button
      type="button"
      className={`${styles.buttonReset} ${styles.sectionHeader}`}
      onClick={onToggle}
      aria-expanded={isOpen}
      aria-controls={`${id}-content`}
      id={`${id}-header`}
    >
      <span className={styles.sectionTitle}>{title}</span>
      <span className={styles.sectionHeaderRight}>
        {headerContent}
        <ExpandLessIcon
          className={`${styles.caret} ${isOpen ? styles.caretOpen : ''}`}
        />
      </span>
    </button>
    <div
      id={`${id}-content`}
      className={`${styles.sectionBody} ${isOpen ? styles.sectionBodyOpen : ''}`}
      role="region"
      aria-labelledby={`${id}-header`}
      aria-hidden={!isOpen}
    >
      {children}
    </div>
  </div>
);

type SelectFieldProps = {
  label: string;
  value: string;
  options: string[];
  onChange: (value: string) => void;
};

const SelectField = ({ label, value, options, onChange }: SelectFieldProps) => {
  const id = useId();

  return (
    <div className={styles.selectField}>
      <label className={styles.selectLabel} htmlFor={id}>
        {label}
      </label>
      <select
        id={id}
        className={styles.selectInput}
        value={value}
        onChange={(event) => onChange(event.target.value)}
      >
        {options.map((option) => (
          <option key={option} value={option}>
            {option}
          </option>
        ))}
      </select>
      <KeyboardArrowDownIcon className={styles.selectIcon} />
    </div>
  );
};

export const DriversManagementDrawer = ({
  open,
  onClose,
}: DriversManagementDrawerProps) => {
  const { openModal } = useModal();
  const closeAnimationDurationMs = 400;
  const [isVisible, setIsVisible] = useState(open);
  const [isAnimatingOpen, setIsAnimatingOpen] = useState(false);

  const [filtersOpen, setFiltersOpen] = useState(false);
  const [consultOpen, setConsultOpen] = useState(true);
  const [sendOpen, setSendOpen] = useState(false);
  const [printOpen, setPrintOpen] = useState(false);
  const [exportOpen, setExportOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);

  const driverDefaultFilter = 'Todos';
  const availabilityDefaultFilter = 'Todos';
  const selectionDefaultFilter = 'Casa e Externos';
  const zoneDefaultFilter = 'Todas';

  const driverOptions = ['Todos', 'Rita Sofia', 'Gabriel Santos'];
  const availabilityOptions = [
    'Todos',
    'Escalado',
    'Não Escalado',
    'Disponível',
  ];
  const selectionOptions = ['Casa', 'Externos', 'Casa e Externos'];
  const zoneOptions = ['Todas', 'Norte', 'Centro', 'Sul'];

  const [applyDefaultFilters, setApplyDefaultFilters] = useState(true);
  const [driverFilter, setDriverFilter] = useState(driverDefaultFilter);
  const [availabilityFilter, setAvailabilityFilter] = useState('Não Escalado');
  const [selectionFilter, setSelectionFilter] = useState(selectionDefaultFilter);
  const [zoneFilter, setZoneFilter] = useState(zoneDefaultFilter);

  const filterSectionId = useId();
  const consultSectionId = useId();
  const sendSectionId = useId();
  const printSectionId = useId();
  const exportSectionId = useId();
  const settingsSectionId = useId();

  const driversWithoutRestLabel =
    'Motoristas Sem Folgas h\u00e1 mais de 6 Dias';
  const driversVehiclesAvailabilityLabel =
    'Viaturas e Motoristas Dispon\u00edveis';
  const restAfterWorkLabel =
    'Descansos ap\u00f3s Folga / Complementar Trabalhada';
  const driverServiceTotalsLabel = 'Serviços e Totais por Motorista';
  const chapasPorMotoristasLabel = 'Chapas por Motoristas';

  const consultItems = [
    'Mapa de Motoristas',
    driversWithoutRestLabel,
    'Motoristas que não descansaram o regulamentado de ontem para hoje',
    restAfterWorkLabel,
    driversVehiclesAvailabilityLabel,
    driverServiceTotalsLabel,
    'Serviços por Tipo de Tacógrafo',
  ];
  const sendItems = ['Declarações de Atividade', 'Chapas por Motoristas'];
  const printItems = [
    driverServiceTotalsLabel,
    'Serviços por Tipo de Tacógrafo',
    chapasPorMotoristasLabel,
  ];
  const exportItems = [driverServiceTotalsLabel];
  const settingsActionLabel = 'Alterar Motoristas não Escalados';

  const filtersApplied = useMemo(() => {
    let count = 0;
    if (driverFilter !== driverDefaultFilter) {
      count += 1;
    }
    if (availabilityFilter !== availabilityDefaultFilter) {
      count += 1;
    }
    if (selectionFilter !== selectionDefaultFilter) {
      count += 1;
    }
    if (zoneFilter !== zoneDefaultFilter) {
      count += 1;
    }
    return count;
  }, [
    availabilityDefaultFilter,
    availabilityFilter,
    driverDefaultFilter,
    driverFilter,
    selectionDefaultFilter,
    selectionFilter,
    zoneDefaultFilter,
    zoneFilter,
  ]);

  const handleClearFilters = () => {
    setDriverFilter(driverDefaultFilter);
    setAvailabilityFilter(availabilityDefaultFilter);
    setSelectionFilter(selectionDefaultFilter);
    setZoneFilter(zoneDefaultFilter);
  };

  const openDriverServiceTotalsModal = (
    footerVariant: DriverServiceTotalsModalData['footerVariant'] = 'export',
  ) => {
    const modalData: DriverServiceTotalsModalData = {
      driverId: 1,
      startDate: '02/05/2024',
      endDate: '08/05/2024',
      filial: 'Transol',
      driver: 'Alexandre Gomes',
      includeScheduleTime: true,
      footerVariant,
      driverCost: '0,00',
      services: [],
      totals: [],
      stats: {
        kmsCarga: '1890',
        kmsVazio: '246',
        totalValue: '930,90',
      },
    };

    openModal('Servicos e Totais por Motorista', modalData, []);
  };

  const openActivityDeclarationsModal = (
    initialTab: ActivityDeclarationsModalData['initialTab'] = 'toIssue',
  ) => {
    const modalData: ActivityDeclarationsModalData = {
      toIssue: [],
      issued: [],
      initialTab,
    };

    openModal('Declaracoes de Atividade', modalData, []);
  };

  const openChapasPorMotoristasModal = () => {
    const modalData: ChapasPorMotoristasModalData = {
      startDate: '01/11/2024',
      endDate: '30/11/2024',
      motorista: 'Anabela, Isabel, Rui (+2)',
      sazonalidade: 'Todas',
      chapa: '0003, 0016',
      motoristas: ['Anabela, Isabel, Rui (+2)', 'Todos'],
      sazonalidades: ['Todas', 'Alta', 'Baixa'],
      chapas: ['0003, 0016', '0003', '0016'],
      rows: [],
    };

    openModal('Chapas por Motoristas', modalData, []);
  };

  const openNonScheduledDriversModal = () => {
    const modalData: NonScheduledDriversModalData = {
      message: 'Atualizar folgas atrasadas dos Motoristas Não Escalados?',
      primaryLabel: 'Atualizar',
      secondaryLabel: 'Cancelar',
    };
    openModal('Alterar Motoristas não Escalados', modalData, []);
  };

  const handleConsultItemClick = (item: string) => {
    if (item === 'Serviços por Tipo de Tacógrafo') {
      const modalData: TachographServiceTypeModalData = {
        rows: [],
      };

      openModal('Serviços por Tipo de Tacógrafo', modalData, []);
      return;
    }

    if (item === driversWithoutRestLabel) {
      const modalData: DriversWithoutRestModalData = {
        rows: [],
        thresholdDays: 6,
        totalDrivers: 17,
        totalRows: 13,
        rowsPerPage: 10,
      };

      openModal('Motoristas sem folga ha mais de 6 dias', modalData, []);
      return;
    }

    if (item === restAfterWorkLabel) {
      const modalData: RestAfterWorkModalData = {
        rows: [],
      };

      openModal(restAfterWorkLabel, modalData, []);
      return;
    }

    if (item === driverServiceTotalsLabel) {
      openDriverServiceTotalsModal('export');
      return;
    }

    if (item !== driversVehiclesAvailabilityLabel) {
      return;
    }

    const modalData: DriversVehiclesAvailabilityModalData = {
      startDate: '02/05/2024 13:15',
      endDate: '03/05/2024 16:15',
      empresa: 'Transol',
      filial: 'Transol',
      lotacaoOperador: 'Superior ou Igual a',
      lotacao: '40',
      anoViatura: 'Posterior a 2020',
      drivers: [
        {
          id: 'driver-1',
          motorista: 'Beatriz Silva',
          numMec: '298',
          ultimoServicoInicio: '02/05/2024 15:00',
          ultimoServicoFim: '02/05/2024 16:00',
          origem: 'Faro',
          destino: 'Portim\u00e3o',
          numFrota: '0062',
          telefone: '910000000',
          inicioPosterior: '02/05/2024 17:00',
          fim: '',
        },
        {
          id: 'driver-2',
          motorista: 'Carlos Cardoso',
          numMec: '123',
          ultimoServicoInicio: '02/05/2024 15:00',
          ultimoServicoFim: '02/05/2024 16:00',
          origem: 'Faro',
          destino: 'Portim\u00e3o',
          numFrota: '0062',
          telefone: '910000000',
          inicioPosterior: '02/05/2024 17:00',
          fim: '',
        },
        {
          id: 'driver-3',
          motorista: 'Rui Henriques',
          numMec: '098',
          ultimoServicoInicio: '02/05/2024 15:00',
          ultimoServicoFim: '02/05/2024 16:00',
          origem: 'Faro',
          destino: 'Portim\u00e3o',
          numFrota: '0062',
          telefone: '910000000',
          inicioPosterior: '02/05/2024 17:00',
          fim: '',
        },
        {
          id: 'driver-4',
          motorista: 'Marco Alves',
          numMec: '765',
          ultimoServicoInicio: '02/05/2024 15:00',
          ultimoServicoFim: '02/05/2024 16:00',
          origem: 'Faro',
          destino: 'Portim\u00e3o',
          numFrota: '0062',
          telefone: '910000000',
          inicioPosterior: '02/05/2024 17:00',
          fim: '',
        },
      ],
      vehicles: [
        {
          id: 'vehicle-1',
          numFrota: '0062',
          lotacao: '55',
          ano: '2021',
          ultimoServicoInicio: '02/05/2024 15:00',
          ultimoServicoFim: '02/05/2024 16:00',
          origem: 'Faro',
          destino: 'Portim\u00e3o',
          numMec: '199',
          motorista: 'Anabela Ramos',
          telefone: '910000000',
          folga: '',
        },
        {
          id: 'vehicle-2',
          numFrota: '0021',
          lotacao: '40',
          ano: '2021',
          ultimoServicoInicio: '02/05/2024 15:00',
          ultimoServicoFim: '02/05/2024 16:00',
          origem: 'Faro',
          destino: 'Portim\u00e3o',
          numMec: '232',
          motorista: 'Miguel Marques',
          telefone: '910000000',
          folga: '',
        },
        {
          id: 'vehicle-3',
          numFrota: '0129',
          lotacao: '55',
          ano: '2024',
          ultimoServicoInicio: '02/05/2024 15:00',
          ultimoServicoFim: '02/05/2024 16:00',
          origem: 'Faro',
          destino: 'Portim\u00e3o',
          numMec: '564',
          motorista: 'Jorge Santos',
          telefone: '910000000',
          folga: '',
        },
      ],
    };

    openModal('Disponibilidade de Motoristas e Viaturas', modalData, []);
  };

  const handlePrintItemClick = (item: string) => {
    if (item === driverServiceTotalsLabel) {
      openDriverServiceTotalsModal('export');
      return;
    }
    if (item === 'Declarações de Atividade') {
      openActivityDeclarationsModal('issued');
      return;
    }
    if (item === 'Serviços por Tipo de Tacógrafo') {
      const modalData: TachographServiceTypeModalData = {
        rows: [],
      };

      openModal('Serviços por Tipo de Tacógrafo', modalData, []);
      return;
    }
    if (item === chapasPorMotoristasLabel) {
      openChapasPorMotoristasModal();
    }
  };

  const handleExportItemClick = (item: string) => {
    if (item === driverServiceTotalsLabel) {
      openDriverServiceTotalsModal('export');
    }
  };

  const handleSendItemClick = (item: string) => {
    if (item === chapasPorMotoristasLabel) {
      openChapasPorMotoristasModal();
      return;
    }
    if (item === 'Declarações de Atividade') {
      openActivityDeclarationsModal('toIssue');
    }
  };

  useEffect(() => {
    let timeoutId: number | undefined;
    let frameId: number | undefined;
    let frameId2: number | undefined;

    if (open) {
      setIsVisible(true);
      setFiltersOpen(false);
      setConsultOpen(true);
      setSendOpen(false);
      setPrintOpen(false);
      setExportOpen(false);
      setSettingsOpen(false);
      setIsAnimatingOpen(false);
      if (typeof window === 'undefined') {
        setIsAnimatingOpen(true);
        return undefined;
      }
      frameId = window.requestAnimationFrame(() => {
        frameId2 = window.requestAnimationFrame(() => {
          setIsAnimatingOpen(true);
        });
      });
      return () => {
        if (frameId !== undefined) {
          window.cancelAnimationFrame(frameId);
        }
        if (frameId2 !== undefined) {
          window.cancelAnimationFrame(frameId2);
        }
      };
    }

    setIsAnimatingOpen(false);
    timeoutId = window.setTimeout(() => {
      setIsVisible(false);
    }, closeAnimationDurationMs);

    return () => {
      if (timeoutId !== undefined) {
        window.clearTimeout(timeoutId);
      }
    };
  }, [open, closeAnimationDurationMs]);

  if (!isVisible) {
    return null;
  }

  return (
    <aside
      className={`${styles.drawer} ${styles.drawerLeft} ${isAnimatingOpen ? styles.drawerOpen : styles.drawerClosed}`}
      aria-label="Gerir Motoristas"
      aria-hidden={!open}
    >
      <div className={styles.header}>
        <span className={styles.title}>Gerir Motoristas</span>
        <button
          type="button"
          className={`${styles.buttonReset} ${styles.closeButton}`}
          onClick={onClose}
          aria-label="Fechar painel"
        >
          <CloseRoundedIcon className={styles.closeIcon} />
        </button>
      </div>

      <div className={styles.content}>
        <AccordionSection
          id={filterSectionId}
          title="Filtrar"
          isOpen={filtersOpen}
          onToggle={() => setFiltersOpen((prev) => !prev)}
          headerContent={
            <span
              className={styles.filterActions}
              style={{
                opacity: filtersApplied > 0 ? 1 : 0,
                pointerEvents: filtersApplied > 0 ? 'auto' : 'none',
              }}
            >
              <button
                type="button"
                className={`${styles.buttonReset} ${styles.filterClear}`}
                onClick={handleClearFilters}
              >
                Limpar Filtros
              </button>
              <span className={styles.filterBadge}>{filtersApplied}</span>
            </span>
          }
        >
          <div className={styles.checkboxRowRight}>
            <FormControlLabel
              className={styles.checkboxLabel}
              control={
                <Checkbox
                  checked={applyDefaultFilters}
                  onChange={(event) =>
                    setApplyDefaultFilters(event.target.checked)
                  }
                  size="small"
                  sx={{ padding: '4px' }}
                />
              }
              label="Aplicar estes Filtros por defeito"
              sx={{
                margin: 0,
                '.MuiTypography-root': {
                  fontFamily: 'Poppins',
                  fontSize: '13px',
                  fontWeight: 500,
                  lineHeight: 1.3,
                  color: '#092056',
                },
              }}
            />
          </div>

          <div className={styles.fieldStack}>
            <SelectField
              label="Motorista"
              value={driverFilter}
              options={driverOptions}
              onChange={setDriverFilter}
            />
            <SelectField
              label="Situação / Disponibilidade"
              value={availabilityFilter}
              options={availabilityOptions}
              onChange={setAvailabilityFilter}
            />
            <SelectField
              label="Seleção de Motoristas"
              value={selectionFilter}
              options={selectionOptions}
              onChange={setSelectionFilter}
            />
            <SelectField
              label="Zona"
              value={zoneFilter}
              options={zoneOptions}
              onChange={setZoneFilter}
            />
          </div>
        </AccordionSection>

        <AccordionSection
          id={consultSectionId}
          title="Consultar"
          isOpen={consultOpen}
          onToggle={() => setConsultOpen((prev) => !prev)}
          className={styles.sectionCompact}
        >
          <div className={styles.consultList}>
            {consultItems.map((item) => (
              <button
                key={item}
                type="button"
                className={styles.consultItem}
                onClick={() => handleConsultItemClick(item)}
              >
                <span className={styles.consultText}>{item}</span>
                <span className={styles.consultIcon}>
                  <ChevronRightIcon fontSize="inherit" />
                </span>
              </button>
            ))}
          </div>
        </AccordionSection>

        <AccordionSection
          id={sendSectionId}
          title="Enviar"
          isOpen={sendOpen}
          onToggle={() => setSendOpen((prev) => !prev)}
          className={styles.sectionCompact}
        >
          <div className={styles.consultList}>
            {sendItems.map((item) => (
              <button
                key={item}
                type="button"
                className={styles.consultItem}
                onClick={() => handleSendItemClick(item)}
              >
                <span className={styles.consultText}>{item}</span>
                <span className={styles.consultIcon}>
                  <ChevronRightIcon fontSize="inherit" />
                </span>
              </button>
            ))}
          </div>
        </AccordionSection>

        <AccordionSection
          id={printSectionId}
          title="Imprimir"
          isOpen={printOpen}
          onToggle={() => setPrintOpen((prev) => !prev)}
          className={styles.sectionCompact}
        >
          <div className={styles.consultList}>
            {printItems.map((item) => (
              <button
                key={item}
                type="button"
                className={styles.consultItem}
                onClick={() => handlePrintItemClick(item)}
              >
                <span className={styles.consultText}>{item}</span>
                <span className={styles.consultIcon}>
                  <ChevronRightIcon fontSize="inherit" />
                </span>
              </button>
            ))}
          </div>
        </AccordionSection>

        <AccordionSection
          id={exportSectionId}
          title="Exportar"
          isOpen={exportOpen}
          onToggle={() => setExportOpen((prev) => !prev)}
          className={styles.sectionCompact}
        >
          <div className={styles.consultList}>
            {exportItems.map((item) => (
              <button
                key={item}
                type="button"
                className={styles.consultItem}
                onClick={() => handleExportItemClick(item)}
              >
                <span className={styles.consultText}>{item}</span>
                <span className={styles.consultIcon}>
                  <ChevronRightIcon fontSize="inherit" />
                </span>
              </button>
            ))}
          </div>
        </AccordionSection>

        <AccordionSection
          id={settingsSectionId}
          title="Definições"
          isOpen={settingsOpen}
          onToggle={() => setSettingsOpen((prev) => !prev)}
          className={styles.sectionCompact}
        >
          <button
            type="button"
            className={styles.settingsAction}
            onClick={openNonScheduledDriversModal}
          >
            <span className={styles.settingsActionText}>
              {settingsActionLabel}
            </span>
            <span className={styles.settingsActionIcon}>
              <ChevronRightIcon fontSize="inherit" />
            </span>
          </button>
        </AccordionSection>
      </div>
    </aside>
  );
};

export default DriversManagementDrawer;
